import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  ChipProps,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import Sidebar from "../components/sidebar/Sidebar";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { setToastMessage } from "../store/slices/app/appSlice";
import { useUserRole } from "../hooks/useUserRole";
import {
  DataHealthItem,
  DataHealthLineState,
  useGetDataHealthQuery,
  useRefreshDataHealthMutation,
} from "../store/apis/briefingApi";

const ALLOWED_ROLES = ["ADMIN"];

/**
 * De más a menos grave, el mismo orden en que el backend devuelve las líneas:
 * primero las que tienen problema y luego las que están bien.
 */
export const STATE_ORDER: DataHealthLineState[] = [
  "ERROR",
  "NOT_CLOSED",
  "STALE",
  "NEVER_SYNCED",
  "NO_DATA",
  "MISSING_DATES",
  "UNLINKED",
  "MANUAL_OUTDATED",
  "IN_PROGRESS",
  "PENDING_REPORT",
  "NOT_STARTED",
  "DIRECT_IN_PROGRESS",
  "OK",
];

const STATE_COLORS: Record<DataHealthLineState, ChipProps["color"]> = {
  ERROR: "error",
  NOT_CLOSED: "warning",
  STALE: "warning",
  NEVER_SYNCED: "warning",
  NO_DATA: "warning",
  MISSING_DATES: "default",
  UNLINKED: "default",
  MANUAL_OUTDATED: "default",
  IN_PROGRESS: "info",
  PENDING_REPORT: "info",
  NOT_STARTED: "default",
  DIRECT_IN_PROGRESS: "default",
  OK: "success",
};

const ALL = "ALL";

// El filtro elegido se queda en la lista aunque el periodo nuevo no lo tenga,
// para no perderlo al cambiar de mes.
const keepSelected = (values: string[], selected: string) =>
  selected === ALL || values.includes(selected)
    ? values
    : [...values, selected];

const DAY_FORMAT = "YYYY-MM-DD";
/** El backend no acepta periodos más largos. */
const MAX_RANGE_DAYS = 366;

/** Lo mismo que mira el correo diario: lo que ha estado activo en los últimos 30 días. */
const last30Days = () => ({
  from: dayjs().subtract(30, "day").format(DAY_FORMAT),
  to: dayjs().format(DAY_FORMAT),
});

const formatDateTime = (iso?: string | null) =>
  iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "-";

// Las fechas de los goals y de las métricas son días a medianoche UTC: se
// enseña el día tal cual, sin moverlo de zona horaria.
const formatDay = (iso?: string | null) =>
  iso ? dayjs(iso.slice(0, 10)).format("DD/MM/YYYY") : "-";

const DataHealth: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { role } = useUserRole();
  const canView = !!role && ALLOWED_ROLES.includes(role);

  const [period, setPeriod] = useState(last30Days);
  const [onlyProblems, setOnlyProblems] = useState(true);

  const periodError = useMemo(() => {
    const from = dayjs(period.from);
    const to = dayjs(period.to);
    if (!period.from || !period.to || !from.isValid() || !to.isValid()) {
      return t("dataHealth.period.missing", "Choose both dates");
    }
    if (from.isAfter(to)) {
      return t("dataHealth.period.reversed", "The start is after the end");
    }
    if (to.diff(from, "day") + 1 > MAX_RANGE_DAYS) {
      return t("dataHealth.period.tooLong", "At most one year");
    }
    return null;
  }, [period, t]);

  // El mes del selector, si el periodo es justo un mes entero.
  const monthValue = useMemo(() => {
    const start = dayjs(period.from);
    return start.isValid() &&
      period.from === start.startOf("month").format(DAY_FORMAT) &&
      period.to === start.endOf("month").format(DAY_FORMAT)
      ? start.format("YYYY-MM")
      : "";
  }, [period]);

  const selectMonth = (value: string) => {
    const month = dayjs(`${value}-01`);
    if (!value || !month.isValid()) return;
    setPeriod({
      from: month.startOf("month").format(DAY_FORMAT),
      to: month.endOf("month").format(DAY_FORMAT),
    });
  };

  const { data, isLoading, isFetching, error, refetch } = useGetDataHealthQuery(
    {
      from: period.from,
      to: period.to,
      ...(onlyProblems ? {} : { all: true }),
    },
    { skip: !canView || !!periodError }
  );
  const [refreshDataHealth, { isLoading: isRefreshing }] =
    useRefreshDataHealthMutation();

  const [stateFilter, setStateFilter] = useState<string>(ALL);
  const [companyFilter, setCompanyFilter] = useState<string>(ALL);
  const [platformFilter, setPlatformFilter] = useState<string>(ALL);
  const [search, setSearch] = useState("");

  const report = data?.data;
  const items = useMemo(() => report?.items ?? [], [report]);

  const companies = useMemo(
    () =>
      keepSelected(
        [...new Set(items.map((i) => i.companyName))],
        companyFilter
      ).sort((a, b) => a.localeCompare(b, "es")),
    [items, companyFilter]
  );
  const platforms = useMemo(
    () =>
      keepSelected(
        [...new Set(items.map((i) => i.internalPlatform ?? i.platform))],
        platformFilter
      ).sort(),
    [items, platformFilter]
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter(
      (i) =>
        (stateFilter === ALL || i.state === stateFilter) &&
        (companyFilter === ALL || i.companyName === companyFilter) &&
        (platformFilter === ALL ||
          (i.internalPlatform ?? i.platform) === platformFilter) &&
        (!needle ||
          i.briefingName.toLowerCase().includes(needle) ||
          i.lineName.toLowerCase().includes(needle))
    );
  }, [items, stateFilter, companyFilter, platformFilter, search]);

  const refreshableIds = useMemo(
    () => [
      ...new Set(
        filtered
          .filter((i) => i.refreshable && i.internalCampaignId)
          .map((i) => i.internalCampaignId as string)
      ),
    ],
    [filtered]
  );

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  const stateLabel = (state: DataHealthLineState) =>
    t(`dataHealth.states.${state}`, state);

  const describe = (item: DataHealthItem): string => {
    switch (item.state) {
      case "ERROR":
        return item.rateLimited
          ? `${item.errorSync ?? ""} · ${t(
              "dataHealth.detail.autoRetry",
              "retried automatically every 30 min"
            )}`
          : item.errorSync || t("dataHealth.detail.errorNoMessage", "Error");
      case "NOT_CLOSED":
        return t("dataHealth.detail.notClosed", {
          end: formatDay(item.endDate),
          sync: formatDateTime(item.lastSyncAt),
          defaultValue:
            "Ended on {{end}}; last sync on {{sync}}, before its last day closed",
        });
      case "STALE":
        return t("dataHealth.detail.stale", {
          sync: formatDateTime(item.lastSyncAt),
          defaultValue: "Last sync: {{sync}}",
        });
      case "NEVER_SYNCED":
        return t(
          "dataHealth.detail.neverSynced",
          "Linked but never synced successfully"
        );
      case "NO_DATA":
        return t(
          "dataHealth.detail.noData",
          "Syncs without errors but the platform returns no data: check the ID and the account"
        );
      case "MISSING_DATES":
        return t(
          "dataHealth.detail.missingDates",
          "The linked campaign has no dates and is not synced"
        );
      case "UNLINKED":
        return t("dataHealth.detail.unlinked", {
          end: formatDay(item.endDate),
          defaultValue:
            "Ended on {{end}}: link it or fill in its manual data in the WIP",
        });
      case "MANUAL_OUTDATED":
        return t("dataHealth.detail.manualOutdated", {
          asOf: formatDay(item.manualMetricsAsOf),
          end: formatDay(item.endDate),
          defaultValue: "Manual data until {{asOf}}; the line ended on {{end}}",
        });
      case "IN_PROGRESS":
        return t("dataHealth.detail.inProgress", "Syncing right now");
      case "PENDING_REPORT":
        return t(
          "dataHealth.detail.pendingReport",
          "Report requested: the data arrives when the platform generates it"
        );
      case "NOT_STARTED":
        return t("dataHealth.detail.notStarted", {
          start: formatDay(item.startDate),
          defaultValue: "Starts on {{start}}",
        });
      case "DIRECT_IN_PROGRESS":
        return t(
          "dataHealth.detail.directInProgress",
          "Direct buy running: its manual data is required when it ends"
        );
      default:
        return "";
    }
  };

  const handleRefresh = async (internalCampaignIds: string[]) => {
    try {
      const response = await refreshDataHealth({
        internalCampaignIds,
      }).unwrap();
      const result = response.data;
      if (!result) return;
      const parts = [
        t("dataHealth.refresh.queued", {
          count: result.queued,
          defaultValue: "{{count}} sent to update",
        }),
      ];
      if (result.recentlyRequested > 0) {
        parts.push(
          t("dataHealth.refresh.recent", {
            count: result.recentlyRequested,
            defaultValue: "{{count}} already requested in the last minutes",
          })
        );
      }
      if (result.remaining > 0) {
        parts.push(
          t("dataHealth.refresh.remaining", {
            count: result.remaining,
            defaultValue: "{{count}} left: press again",
          })
        );
      }
      dispatch(
        setToastMessage({
          message: parts.join(" · "),
          severity: result.failed.length > 0 ? "warning" : "success",
        })
      );
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "dataHealth.refresh.error",
            "Could not request the update"
          ),
          severity: "error",
        })
      );
    }
  };

  const selectFilter = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: { value: string; label: string }[]
  ) => (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
      >
        <MenuItem value={ALL}>{t("dataHealth.filters.all", "All")}</MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {t("dataHealth.title", "Data health")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  "dataHealth.subtitle",
                  "Every campaign needs its data until its end date, at the latest one day after. Lines of briefings in production or finalized that run at some point within the period."
                )}
              </Typography>
              {report && (
                <Typography variant="caption" color="text.secondary">
                  {t("dataHealth.checked", {
                    date: formatDateTime(report.checkedAt),
                    checked: report.checkedLines,
                    problems: report.problems,
                    defaultValue:
                      "Checked at {{date}} · {{checked}} lines reviewed, {{problems}} with problems",
                  })}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                startIcon={
                  isRefreshing ? <CircularProgress size={18} /> : <SyncIcon />
                }
                disabled={isRefreshing || refreshableIds.length === 0}
                onClick={() => handleRefresh(refreshableIds)}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("dataHealth.refreshAll", {
                  count: refreshableIds.length,
                  defaultValue: "Update the {{count}} that can be requested",
                })}
              </Button>
              <Tooltip title={t("dataHealth.reload", "Check again")}>
                <span>
                  <IconButton
                    onClick={() => refetch()}
                    disabled={isFetching || !!periodError}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            mt={2}
          >
            <TextField
              size="small"
              type="month"
              label={t("dataHealth.period.month", "Month")}
              value={monthValue}
              onChange={(e) => selectMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label={t("dataHealth.period.from", "From")}
              value={period.from}
              onChange={(e) =>
                setPeriod((p) => ({ ...p, from: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              error={!!periodError}
            />
            <TextField
              size="small"
              type="date"
              label={t("dataHealth.period.to", "To")}
              value={period.to}
              onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              error={!!periodError}
              helperText={periodError ?? undefined}
            />
            <Button size="small" onClick={() => setPeriod(last30Days())}>
              {t("dataHealth.period.last30", "Last 30 days")}
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={onlyProblems}
                  onChange={(e) => {
                    setOnlyProblems(e.target.checked);
                    setStateFilter(ALL);
                  }}
                />
              }
              label={t("dataHealth.onlyProblems", "Only with problems")}
            />
          </Stack>

          {report && items.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              mt={2}
            >
              {STATE_ORDER.filter((s) => report.byState[s] > 0).map((s) => (
                <Chip
                  key={s}
                  size="small"
                  color={STATE_COLORS[s]}
                  variant={stateFilter === s ? "filled" : "outlined"}
                  label={`${stateLabel(s)}: ${report.byState[s]}`}
                  onClick={() => setStateFilter(stateFilter === s ? ALL : s)}
                />
              ))}
            </Stack>
          )}
        </Paper>

        {error && (
          <Alert severity="error">
            {t("dataHealth.loadError", "Unable to load the data health report")}
          </Alert>
        )}

        {isLoading ? (
          <Stack direction="row" justifyContent="center" py={6}>
            <CircularProgress />
          </Stack>
        ) : report && items.length === 0 ? (
          onlyProblems ? (
            <Alert severity="success">
              {t("dataHealth.allGood", "Every line is up to date.")}
            </Alert>
          ) : (
            <Alert severity="info">
              {t("dataHealth.empty", "No lines in this period.")}
            </Alert>
          )
        ) : report ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[200]}`,
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              mb={2}
            >
              {selectFilter(
                t("dataHealth.filters.state", "Status"),
                stateFilter,
                setStateFilter,
                STATE_ORDER.filter((s) => report.byState[s] > 0).map((s) => ({
                  value: s,
                  label: stateLabel(s),
                }))
              )}
              {selectFilter(
                t("dataHealth.filters.company", "Company"),
                companyFilter,
                setCompanyFilter,
                companies.map((c) => ({ value: c, label: c }))
              )}
              {selectFilter(
                t("dataHealth.filters.platform", "Platform"),
                platformFilter,
                setPlatformFilter,
                platforms.map((p) => ({ value: p, label: p }))
              )}
              <TextField
                size="small"
                label={t("dataHealth.filters.search", "Briefing or line")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Stack>

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("dataHealth.columns.company", "Company")}
                    </TableCell>
                    <TableCell>
                      {t("dataHealth.columns.briefing", "Briefing")}
                    </TableCell>
                    <TableCell>
                      {t("dataHealth.columns.line", "Line")}
                    </TableCell>
                    <TableCell>
                      {t("dataHealth.columns.platform", "Platform")}
                    </TableCell>
                    <TableCell>
                      {t("dataHealth.columns.start", "Start")}
                    </TableCell>
                    <TableCell>{t("dataHealth.columns.end", "End")}</TableCell>
                    <TableCell>
                      {t("dataHealth.columns.dataUntil", "Data until")}
                    </TableCell>
                    <TableCell>
                      {t("dataHealth.columns.state", "Status")}
                    </TableCell>
                    <TableCell>
                      {t("dataHealth.columns.detail", "Detail")}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.associatedCampaignId}>
                      <TableCell>{item.companyName}</TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/briefings/${item.briefingId}`}
                        >
                          {item.briefingName}
                        </Link>
                      </TableCell>
                      <TableCell
                        sx={{ maxWidth: 280, wordBreak: "break-word" }}
                      >
                        {item.lineName}
                      </TableCell>
                      <TableCell>
                        {item.internalPlatform ?? item.platform}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDay(item.startDate)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDay(item.endDate)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDay(item.dataUntil)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={STATE_COLORS[item.state]}
                          variant={
                            STATE_COLORS[item.state] === "default"
                              ? "outlined"
                              : "filled"
                          }
                          label={stateLabel(item.state)}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ maxWidth: 380, wordBreak: "break-word" }}
                      >
                        <Typography variant="body2">
                          {describe(item)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.refreshable && item.internalCampaignId && (
                          <Tooltip
                            title={t(
                              "dataHealth.refreshOne",
                              "Request this line again"
                            )}
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label={t(
                                  "dataHealth.refreshOne",
                                  "Request this line again"
                                )}
                                disabled={isRefreshing}
                                onClick={() =>
                                  handleRefresh([
                                    item.internalCampaignId as string,
                                  ])
                                }
                              >
                                <SyncIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" mt={2}>
                {t("dataHealth.noMatches", "No line matches the filters.")}
              </Typography>
            )}
          </Paper>
        ) : null}
      </Box>
    </Box>
  );
};

export default DataHealth;
